import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';

// 区块容器样式
const SectionContainer = styled('section', {
  width: '100%',
  padding: 'var(--spacing-xxl) 0',
  backgroundColor: 'var(--background-color)'
});

// 内容容器样式
const ContentContainer = styled('div', {
  maxWidth: 'var(--container-width)',
  margin: '0 auto',
  padding: '0 var(--spacing-lg)'
});

// 区块标题样式
const SectionTitle = styled('h2', {
  fontSize: 'var(--font-size-xxl)',
  fontWeight: 'var(--font-weight-bold)',
  textAlign: 'center',
  marginBottom: 'var(--spacing-md)',
  color: 'var(--text-primary)'
});

// 区块描述样式
const SectionDescription = styled('p', {
  fontSize: 'var(--font-size-md)',
  textAlign: 'center',
  maxWidth: '800px',
  margin: '0 auto var(--spacing-xl)',
  color: 'var(--text-secondary)'
});

// 特点容器样式
const FeaturesGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 'var(--spacing-xl)',
  
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
    gap: 'var(--spacing-lg)'
  }
});

// 特点卡片样式
const FeatureCard = styled('div', {
  borderRadius: 'var(--border-radius-md)',
  padding: '0',
  boxShadow: 'var(--shadow-sm)',
  transition: 'transform 0.4s ease, box-shadow 0.4s ease, opacity 0.5s ease',
  position: 'relative',
  overflow: 'hidden',
  height: '280px',
  opacity: 0,
  transform: 'translateY(30px)',
  
  '&.visible': {
    opacity: 1,
    transform: 'translateY(0)'
  },
  
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    transition: 'transform 0.6s ease',
    transform: 'scale(1)',
    zIndex: -1
  },
  
  '&:hover': {
    transform: 'translateY(-7px)',
    boxShadow: 'var(--shadow-lg)',
    
    '&:before': {
      transform: 'scale(1.1)'
    }
  },
  
  '&:hover TextOverlay': {
    height: '55%',
    backdropFilter: 'blur(5px)'
  },
  
  // 移动端触摸反馈
  '@media (max-width: 768px)': {
    height: '220px',
    
    '&:active': {
      transform: 'scale(0.98)',
      transition: 'transform 0.2s ease'
    }
  }
});

// 文字内容容器（半透明蒙版）
const TextOverlay = styled('div', {
  position: 'absolute',
  bottom: '0',
  left: '0',
  right: '0',
  height: '40%',
  padding: 'var(--spacing-xl)',
  background: 'linear-gradient(to top, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.5))',
  backdropFilter: 'blur(3px)',
  color: 'white',
  transition: 'all 0.4s ease',
  
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '4px',
    background: 'var(--primary-color)',
    transform: 'scaleX(0)',
    transformOrigin: 'left',
    transition: 'transform 0.4s ease',
  },
  
  'FeatureCard:hover &': {
    height: '55%',
    backdropFilter: 'blur(5px)',
    
    '&:after': {
      transform: 'scaleX(1)'
    }
  },
  
  // 移动端适配
  '@media (max-width: 768px)': {
    height: '50%', // 增加移动端文字区域高度
    padding: 'var(--spacing-md)', // 减少padding给文字更多空间
    
    '&:after': {
      height: '3px'
    }
  }
});

// 特点标题样式
const FeatureTitle = styled('h3', {
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'white',
  marginBottom: 'var(--spacing-sm)',
  
  // 移动端字体大小优化
  '@media (max-width: 768px)': {
    fontSize: 'var(--font-size-md)', // 减小移动端标题字体
    marginBottom: 'var(--spacing-xs)' // 减少底部间距
  }
});

// 特点描述样式
const FeatureDescription = styled('p', {
  fontSize: 'var(--font-size-md)',
  color: 'rgba(255, 255, 255, 0.9)',
  lineHeight: 1.6,
  
  // 移动端文字优化
  '@media (max-width: 768px)': {
    fontSize: 'var(--font-size-sm)', // 减小移动端描述字体
    lineHeight: 1.4 // 紧凑行高，节省空间
  }
});

/**
 * 平台亮点部分组件
 * 展示平台的核心特点和优势
 */
const FeaturesSection: React.FC = () => {
  const { t } = useTranslation(); // 使用国际化钩子
  const [visibleCards, setVisibleCards] = useState<boolean[]>([]);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  
  // 初始化卡片可见状态数组
  useEffect(() => {
    setVisibleCards(new Array(4).fill(false));
  }, []);
  
  // 监听滚动事件，检测卡片是否进入视口
  useEffect(() => {
    const observerOptions = {
      root: null, // 使用视口作为参考
      rootMargin: '0px',
      threshold: 0.1 // 当10%的卡片可见时触发
    };
    
    const observerCallback: IntersectionObserverCallback = (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const index = Number(entry.target.getAttribute('data-index'));
          if (!isNaN(index)) {
            setVisibleCards(prev => {
              const newState = [...prev];
              newState[index] = true;
              return newState;
            });
            observer.unobserve(entry.target); // 一旦可见，停止观察
          }
        }
      });
    };
    
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    
    // 观察所有卡片
    const currentCards = cardsRef.current;
    currentCards.forEach(card => {
      if (card) observer.observe(card);
    });
    
    return () => {
      // 清理
      currentCards.forEach(card => {
        if (card) observer.unobserve(card);
      });
    };
  }, []);
  
  // 特点数据 - 使用国际化文本
  const features = [
    {
      icon: '/images/home/home1.jpg', 
      title: t('home.features.feature1.title'),
      description: t('home.features.feature1.description')
    },
    {
      icon: '/images/home/home2.jpg',
      title: t('home.features.feature2.title'),
      description: t('home.features.feature2.description')
    },
    {
      icon: '/images/home/home3.jpg',
      title: t('home.features.feature3.title'),
      description: t('home.features.feature3.description')
    },
    {
      icon: '/images/home/home4.jpg',
      title: t('home.features.feature4.title'),
      description: t('home.features.feature4.description')
    }
  ];
  
  return (
    <SectionContainer>
      <ContentContainer>
        <SectionTitle>{t('home.features.title')}</SectionTitle>
        <SectionDescription>{t('home.features.description')}</SectionDescription>
        
        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard 
              key={index} 
              ref={el => cardsRef.current[index] = el}
              data-index={index}
              className={visibleCards[index] ? 'visible' : ''}
              style={{
                transitionDelay: `${index * 0.1}s`
              }}
            >
              <div style={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                width: '100%', 
                height: '100%', 
                backgroundImage: `url(${feature.icon})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                transition: 'transform 0.6s ease'
              }} />
              <TextOverlay>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </TextOverlay>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </ContentContainer>
    </SectionContainer>
  );
};

export default FeaturesSection; 